import React, { useEffect, useState, useCallback } from 'react';
import { ChevronRight, Sparkles, RefreshCw, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axiosInstance from '../../../utils/axiosConfig';
import { COLOR_CRITICO } from '../gantt/TaskBar';

// ---------- Panel de análisis del proyecto ----------
//
// Arriba, los números duros del cálculo de la red (siempre frescos): cuánto
// se corre el fin del proyecto, fin proyectado vs planificado, tamaño de la
// ruta crítica y cuello de botella. Abajo, la lectura de la IA sobre esos
// mismos números (cacheada 12 h en el backend; se regenera a pedido).

const PREF_KEY = 'pm.red.analisisAbierto';

const fechaLarga = (iso) => (iso
  ? new Date(iso + 'T00:00:00').toLocaleDateString('es-VE', { day: 'numeric', month: 'short', year: 'numeric' }).replace('.', '')
  : '—');

const haceCuanto = (iso) => {
  if (!iso) return '';
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'recién';
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  return `hace ${Math.round(h / 24)} d`;
};

const colorDesviacion = (pct) => {
  if (!pct) return 'text-emerald-600';
  if (pct < 10) return 'text-amber-600';
  return 'text-rose-600';
};

const Dato = ({ etiqueta, valor, detalle, claseValor = 'text-slate-900', children }) => (
  <div className="min-w-0 px-5 py-3.5 first:pl-6">
    <p className="text-xs text-slate-500">{etiqueta}</p>
    {children || (
      <p className={`mt-1 text-xl font-semibold tracking-tight tabular-nums ${claseValor}`}>{valor}</p>
    )}
    {detalle && <p className="mt-0.5 text-xs text-slate-500 truncate">{detalle}</p>}
  </div>
);

// Chip con el código de una tarea; abre la tarea si existe en la red.
const ChipTarea = ({ codigo, onAbrir }) => (
  <button
    onClick={() => onAbrir(codigo)}
    className="inline-flex items-center px-1.5 py-px rounded bg-slate-100 hover:bg-slate-200 text-[11px] font-medium text-slate-700 tabular-nums transition-colors"
  >
    {codigo}
  </button>
);

const AnalisisPanel = ({ proyectoId, resumen, cuello, onAbrirCodigo }) => {
  const [ia, setIa] = useState(null);
  const [cargandoIa, setCargandoIa] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState(null);
  const [abierto, setAbierto] = useState(() => {
    try { return window.localStorage.getItem(PREF_KEY) !== '0'; } catch { return true; }
  });

  useEffect(() => {
    try { window.localStorage.setItem(PREF_KEY, abierto ? '1' : '0'); } catch { /* sin storage */ }
  }, [abierto]);

  useEffect(() => {
    if (!proyectoId) return undefined;
    let vivo = true;
    setCargandoIa(true);
    axiosInstance.get(`/api/proyectos/${proyectoId}/analisis`)
      .then(({ data }) => { if (vivo) setIa(data.ia); })
      .catch((err) => console.error('Error cargando análisis:', err))
      .finally(() => { if (vivo) setCargandoIa(false); });
    return () => { vivo = false; };
  }, [proyectoId]);

  const generar = useCallback(async () => {
    setGenerando(true);
    setError(null);
    try {
      const { data } = await axiosInstance.post(`/api/proyectos/${proyectoId}/analisis/regenerar`);
      setIa(data.ia);
      setAbierto(true);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo generar el análisis. Intenta de nuevo.');
    } finally {
      setGenerando(false);
    }
  }, [proyectoId]);

  if (!resumen) return null;

  const pct = resumen.desviacion_pct || 0;
  const margenCompromiso = resumen.fecha_fin_proyecto && resumen.fin_proyectado
    ? Math.round((new Date(resumen.fecha_fin_proyecto) - new Date(resumen.fin_proyectado)) / 86400000)
    : null;
  const numerosCambiaron = ia?.base && (
    ia.base.desviacion_pct !== resumen.desviacion_pct || ia.base.fin_proyectado !== resumen.fin_proyectado
  );

  return (
    <section className="flex-shrink-0 border-b border-slate-200/70 bg-white">
      {/* Números del cálculo */}
      <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-slate-100">
        <Dato
          etiqueta="Desviación de la ruta crítica"
          valor={`${pct}%`}
          claseValor={colorDesviacion(pct)}
          detalle={resumen.retraso_ruta_dias
            ? `El fin se corre ${resumen.retraso_ruta_dias} d sobre ${resumen.linea_base === 'compromiso' ? 'el compromiso' : 'el plan'}`
            : 'El fin del proyecto no se ha corrido'}
        />
        <Dato
          etiqueta="Fin proyectado"
          valor={fechaLarga(resumen.fin_proyectado)}
          detalle={margenCompromiso != null
            ? `Compromiso ${fechaLarga(resumen.fecha_fin_proyecto)}: ${margenCompromiso >= 0 ? `${margenCompromiso} d de margen` : `${-margenCompromiso} d tarde`}`
            : `Planificado ${fechaLarga(resumen.fin_planificado)}`}
        />
        <Dato
          etiqueta="Ruta crítica"
          valor={`${resumen.criticas_pendientes} de ${resumen.criticas}`}
          detalle={`tareas pendientes, ${resumen.duracion_planificada_dias} d planificados`}
        />
        <Dato etiqueta="Cuello de botella">
          {cuello ? (
            <>
              <button
                onClick={() => onAbrirCodigo(cuello.codigo)}
                className="mt-1 flex items-baseline gap-1.5 max-w-full text-left group"
              >
                <span className="text-sm font-semibold tabular-nums" style={{ color: cuello.critica ? COLOR_CRITICO : '#334155' }}>
                  {cuello.codigo}
                </span>
                <span className="text-sm font-medium text-slate-900 truncate group-hover:underline">{cuello.titulo}</span>
              </button>
              <p className="mt-0.5 text-xs text-slate-500 truncate" title={cuello.motivo}>{cuello.motivo}</p>
            </>
          ) : (
            <p className="mt-1 text-sm text-slate-500">Nada frena el proyecto ahora</p>
          )}
        </Dato>
      </div>

      {resumen.sin_fechas > 0 && (
        <p className="px-6 pb-2 -mt-1 text-xs text-amber-700">
          {resumen.sin_fechas} {resumen.sin_fechas === 1 ? 'tarea no tiene fechas y se cuenta' : 'tareas no tienen fechas y se cuentan'} como 1 día.
        </p>
      )}

      {/* Lectura de la IA */}
      <div className="border-t border-slate-100">
        <div className="flex items-center justify-between px-6 py-2">
          <button
            onClick={() => setAbierto((v) => !v)}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900"
            aria-expanded={abierto}
          >
            <motion.span animate={{ rotate: abierto ? 90 : 0 }} transition={{ duration: 0.15 }} className="inline-flex">
              <ChevronRight size={12} strokeWidth={2.5} />
            </motion.span>
            <Sparkles size={12} className="text-cyan-600" />
            Análisis
            {ia?.generado_en && <span className="font-normal text-slate-400">{haceCuanto(ia.generado_en)}</span>}
          </button>
          <div className="flex items-center gap-2">
            {numerosCambiaron && !generando && (
              <span className="text-xs text-amber-700">Los números cambiaron desde este análisis</span>
            )}
            <button
              onClick={generar}
              disabled={generando}
              className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md disabled:opacity-60 transition-colors"
            >
              <RefreshCw size={11} className={generando ? 'animate-spin' : ''} />
              {generando ? 'Analizando…' : ia ? 'Actualizar análisis' : 'Generar análisis'}
            </button>
          </div>
        </div>

        <AnimatePresence initial={false}>
          {abierto && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="overflow-hidden"
            >
              <div className="px-6 pb-4">
                {error && (
                  <p className="mb-2 inline-flex items-center gap-1.5 text-xs text-rose-600">
                    <AlertCircle size={12} /> {error}
                  </p>
                )}
                {cargandoIa ? (
                  <div className="h-10 w-2/3 rounded bg-slate-100 animate-pulse" />
                ) : !ia ? (
                  <p className="text-sm text-slate-500 max-w-2xl">
                    Genera un análisis para leer en limpio cómo va el proyecto, qué lo está frenando y qué conviene hacer esta semana.
                  </p>
                ) : (
                  <div className="grid gap-x-10 gap-y-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
                    <div className="min-w-0">
                      <p className="text-sm text-slate-800 leading-relaxed max-w-[70ch]">{ia.estatus}</p>
                      {ia.cuello_botella?.explicacion && (
                        <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-[70ch]">
                          {ia.cuello_botella.tarea_codigo && (
                            <><ChipTarea codigo={ia.cuello_botella.tarea_codigo} onAbrir={onAbrirCodigo} />{' '}</>
                          )}
                          {ia.cuello_botella.explicacion}
                        </p>
                      )}
                      {ia.recursos_sobrecargados?.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-slate-500">Personas sobrecargadas</p>
                          <ul className="mt-1 space-y-1">
                            {ia.recursos_sobrecargados.map((r) => (
                              <li key={r.nombre} className="text-sm text-slate-700 leading-snug">
                                <span className="font-medium text-slate-900">{r.nombre}</span>
                                <span className="text-slate-400">: </span>
                                {r.explicacion}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    {ia.acciones?.length > 0 && (
                      <div className="min-w-0 lg:border-l lg:border-slate-100 lg:pl-8">
                        <p className="text-xs font-medium text-slate-500">Qué hacer esta semana</p>
                        <ul className="mt-1.5 space-y-2">
                          {ia.acciones.map((a, i) => (
                            <li key={i} className="flex gap-2 text-sm text-slate-700 leading-snug">
                              <span className="mt-[7px] w-1 h-1 rounded-full bg-cyan-600 flex-shrink-0" />
                              <span>
                                {a.texto}
                                {a.tarea_codigo && <>{' '}<ChipTarea codigo={a.tarea_codigo} onAbrir={onAbrirCodigo} /></>}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default AnalisisPanel;
